import { useEffect, useLayoutEffect, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import { createPortal } from 'react-dom';
import './AiCombo.css';
import { markUserEdit } from '../hooks/useUnsavedGuard';

/** One option in an {@link AiCombo}. `value` is the stored id, `label` the text. */
export interface AiComboOption {
  value: string;
  label: string;
}

interface AiComboProps {
  /** Currently-selected value (id), or null/'' for none. */
  value: string | null;
  /** The option list (id + display label). */
  options: AiComboOption[];
  /** Fired with the new value (null when the N/A row is picked). */
  onChange: (value: string | null) => void;
  /** When supplied, renders the orange ＋ "New" button welded to the dropdown. */
  onNew?: () => void;
  /** Tooltip for the ＋ button. */
  newTitle?: string;
  /** Disable ONLY the ＋ "New" button (e.g. a cascade "+" whose parent is not chosen yet),
   *  while the dropdown itself stays usable. Unset = the ＋ follows `disabled`. */
  newDisabled?: boolean;
  disabled?: boolean;
  /** Shown when nothing is selected (and as the clear row's label). */
  placeholder?: string;
  /** Compact height for in-grid inline-cell editors. */
  compact?: boolean;
  /** Wrap long values and options onto several lines (the box grows, the list is at least 180px wide) instead of cutting
   *  them with an ellipsis — for in-grid row editors, whose cells are narrow. */
  wrap?: boolean;
  /** Extra class on the box (e.g. the required-empty flash). */
  className?: string;
  /** Label of the top "clear" row in the dropdown (defaults to the placeholder). */
  naLabel?: string;
  /**
   * Renders the legacy XAF **eraser** button welded to the right of the dropdown
   * caret. In ALPACA a LookupPropertyEditor over an XPO **reference** (an FK such
   * as Employee / BroughtBy / TurnAroundTime) shows TWO buttons — the caret AND a
   * clear/eraser — while a string field on a checked-list editor shows only the
   * caret. Set this on FK lookups so the pair matches (owner report 2026-09-28).
   */
  erasable?: boolean;
  /** OPT-IN (with `erasable`): draw the clear as the Sample Registration / G5
   *  INLINE ⊗ inside the field — grey, shown only once there is a value, red on
   *  hover — instead of the orange eraser block beside the caret. First used by the
   *  Reagent Preparation module (owner request 2026-11-03: "refer the clear option
   *  in the sample registration form"). Unset = the eraser block (unchanged). */
  clearInline?: boolean;
  /** OPT-IN: the field's caption, when it sits in a sibling cell rather than a
   *  `<label for>`. The trigger is then named "{caption}, {value}" so a screen
   *  reader announces both. Unset = unchanged (the button's text is its name). */
  ariaLabel?: string;
  /** OPT-IN: cap the open list at this many rows (the N/A row included) and scroll
   *  from there. MEASURED from a real row once open, so a font change cannot make
   *  it show one too few. Unset = the stylesheet's 260px cap (unchanged). First
   *  used by the Reagent Prep Notepad's unit lists — five rows, owner request
   *  2026-09-26, carried over from the ComboSelect it replaced (2026-11-03). */
  visibleRows?: number;
}

/**
 * A select-style picker rendered as the XAF LookupPropertyEditor: the value on
 * the left, and the **New (＋)** button welded to the **dropdown** as ONE combined
 * orange control on the right edge. Built on the shared `.ai-lkbox` box so it
 * matches every other lookup field. Clearing is always available from the top row
 * of the dropdown (like XAF's "N/A"); pass `erasable` to ALSO show the legacy
 * eraser button beside the caret, which is what XAF renders for an FK lookup.
 *
 * Drop-in for a native `<select>`: `value` is the id, `onChange` gives the id
 * (or null for the N/A row).
 */
export function AiCombo({
  value, options, onChange, onNew, newTitle, newDisabled, disabled,
  placeholder = 'N/A', compact, wrap, className, naLabel, erasable, clearInline, ariaLabel, visibleRows,
}: AiComboProps) {
  const [open, setOpen] = useState(false);
  /** Keyboard-cursor index while open — 0 = the N/A row, 1..n = options. */
  const [active, setActive] = useState(-1);
  const [pos, setPos] = useState<{ top: number; left: number; width: number } | null>(null);
  const boxRef = useRef<HTMLDivElement>(null);
  const popRef = useRef<HTMLDivElement>(null);

  // FK ids compare case-INSENSITIVELY: option ids may arrive UPPERCASE from SQL
  // while the bound value is a lowercase JSON GUID — a case-sensitive match would
  // never preselect the current row (same rule as the legacy ClientDetail Combo).
  const eq = (a: string | null, b: string | null) => (a ?? '').toLowerCase() === (b ?? '').toLowerCase();
  const selected = options.find((o) => eq(o.value, value));

  const place = () => {
    const r = boxRef.current?.getBoundingClientRect();
    if (!r) return;
    // A wrapping combo sits in a narrow grid cell, so its list is never narrower than 180px (and stays on screen).
    const width = wrap ? Math.max(r.width, 180) : r.width;
    setPos({ top: Math.min(r.bottom + 2, window.innerHeight - 60), left: Math.max(4, Math.min(r.left, window.innerWidth - width - 8)), width });
  };
  useLayoutEffect(() => { if (open) place(); }, [open]);
  // `visibleRows`: size the list to exactly N measured rows (+ its 2px border).
  useLayoutEffect(() => {
    const pop = popRef.current;
    if (!open || !visibleRows || !pop) return;
    const row = pop.querySelector<HTMLElement>('.aic-opt');
    if (row && row.offsetHeight > 0) pop.style.maxHeight = `${row.offsetHeight * visibleRows + 2}px`;
  }, [open, pos, visibleRows]);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (popRef.current?.contains(t) || boxRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    const reanchor = (e: Event) => { if (popRef.current?.contains(e.target as Node)) return; place(); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    document.addEventListener('scroll', reanchor, true);
    window.addEventListener('resize', reanchor);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('scroll', reanchor, true);
      window.removeEventListener('resize', reanchor);
    };
  }, [open]);

  const toggle = () => { if (!disabled) setOpen((o) => !o); };
  const focusTrigger = () => boxRef.current?.querySelector<HTMLButtonElement>('.aic-trigger')?.focus();
  const pick = (v: string | null) => { markUserEdit(boxRef.current); onChange(v); setOpen(false); focusTrigger(); };

  // On open, put the cursor on the current selection (0 = N/A row, else its option).
  useEffect(() => {
    if (!open) return;
    const idx = value ? options.findIndex((o) => eq(o.value, value)) : -1;
    setActive(idx >= 0 ? idx + 1 : 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  // Keep the cursor row scrolled into view as ↑/↓ move it.
  useEffect(() => {
    if (!open || active < 0) return;
    popRef.current?.querySelector('.aic-opt-active')?.scrollIntoView?.({ block: 'nearest' });
  }, [active, open]);

  // Keyboard nav (mouse-free, ADR-0013): this combo has NO filter box, so focus stays
  // on the trigger — closed, Enter/Space/Alt+↓ open it (bare ↑/↓ left to the AppShell
  // spatial nav so they move between fields); open, ↑/↓ move the cursor (0 = N/A row),
  // Home/End jump, Enter picks, Esc closes. stopPropagation so the spatial nav / global
  // hotkeys never act on the same key (the popup is portaled but React events bubble).
  const itemCount = options.length + 1;   // +1 for the N/A row at index 0
  const onKeyDown = (e: ReactKeyboardEvent) => {
    if (disabled) return;
    if (!open) {
      // The trigger is a real <button>, so native Enter/Space already OPEN it — don't
      // also handle them here (that double-toggles). Add only Alt+↓ (ComboBox standard);
      // bare ↑/↓ fall through to the AppShell spatial nav (move between fields).
      if (e.key === 'ArrowDown' && e.altKey) { e.preventDefault(); setOpen(true); }
      return;
    }
    if (e.key === 'ArrowDown')      { e.preventDefault(); e.stopPropagation(); setActive((a) => Math.min(itemCount - 1, a < 0 ? 0 : a + 1)); }
    else if (e.key === 'ArrowUp')   { e.preventDefault(); e.stopPropagation(); setActive((a) => Math.max(0, a < 0 ? 0 : a - 1)); }
    else if (e.key === 'Home')      { e.preventDefault(); e.stopPropagation(); setActive(0); }
    else if (e.key === 'End')       { e.preventDefault(); e.stopPropagation(); setActive(itemCount - 1); }
    // Enter picks the cursor row — preventDefault STOPS the button's native Enter→click
    // (which would re-toggle the popup back open after pick closed it). Space is left to
    // the native button (its keyup click closes the open popup), so it's not handled here.
    else if (e.key === 'Enter')     { e.preventDefault(); e.stopPropagation(); if (active >= 0) pick(active === 0 ? null : (options[active - 1]?.value ?? null)); }
    else if (e.key === 'Escape')    { e.preventDefault(); e.stopPropagation(); setOpen(false); focusTrigger(); }
  };

  return (
    <div
      ref={boxRef}
      className={`ai-lkbox aic${compact ? ' ai-lkbox-sm' : ''}${wrap ? ' aic-wrap' : ''}${disabled ? ' aic-disabled' : ''}${className ? ` ${className}` : ''}`}
      onKeyDown={onKeyDown}
    >
      <button
        type="button"
        className="aic-trigger"
        disabled={disabled}
        onClick={toggle}
        aria-haspopup="listbox"
        aria-expanded={open}
        {...(ariaLabel ? { 'aria-label': `${ariaLabel}, ${selected?.label ?? placeholder}` } : undefined)}
      >
        <span className={selected ? 'aic-val' : 'aic-ph'}>{selected?.label ?? placeholder}</span>
      </button>
      {/* Inline ⊗ clear (clearInline) — the Sample Registration / G5 look: only
          once there is a value, never on a read-only field. */}
      {erasable && clearInline && selected && !disabled && (
        <button
          type="button"
          className="aic-clear-inline"
          tabIndex={-1}
          title="Clear"
          aria-label="Clear"
          onClick={(e) => { e.stopPropagation(); pick(null); }}
        ><i className="ti ti-circle-x" aria-hidden="true" /></button>
      )}
      {onNew && (
        <button
          type="button"
          className="aic-new"
          title={newTitle ?? 'New'}
          disabled={disabled || newDisabled}
          onClick={(e) => { e.stopPropagation(); onNew(); }}
        >＋</button>
      )}
      <button
        type="button"
        className="aic-caret"
        tabIndex={-1}
        aria-label="Toggle dropdown"
        disabled={disabled}
        onClick={toggle}
      ><span className="aic-caret-tri" aria-hidden="true" /></button>
      {/* XAF eraser — the SAME orange group as the caret, to its right (legacy
          button order, verified on the ALPACA ReceivedDate popup). */}
      {erasable && !clearInline && (
        <button
          type="button"
          className="aic-erase"
          tabIndex={-1}
          title="Clear"
          aria-label="Clear"
          disabled={disabled}
          onClick={(e) => { e.stopPropagation(); pick(null); }}
        ><i className="ti ti-eraser" aria-hidden="true" /></button>
      )}

      {open && pos && createPortal(
        <div ref={popRef} role="listbox" className={`aic-pop${wrap ? ' aic-wrap' : ''}`} style={{ top: pos.top, left: pos.left, width: pos.width }}>
          <div className={`aic-opt${!value ? ' on' : ''}${active === 0 ? ' aic-opt-active' : ''}`} role="option" aria-selected={!value}
               onClick={() => pick(null)}>{naLabel ?? placeholder}</div>
          {options.map((o, i) => (
            <div key={o.value} className={`aic-opt${eq(o.value, value) ? ' on' : ''}${active === i + 1 ? ' aic-opt-active' : ''}`} role="option"
                 aria-selected={eq(o.value, value)} onClick={() => pick(o.value)}>{o.label}</div>
          ))}
        </div>, document.body)}
    </div>
  );
}
